import { useState, useCallback, useRef, useEffect } from "react";
import ColasService from "../api/colas.api";
import { construirEntradaTurno } from "../helpers/registro";

/**
 * Hook que da de alta turnos (con pedido, opcionalmente con todos los del cliente,
 * o sin pedido). No genera toasts ni mensajes: relanza el error del backend
 * (.message y .status) para que la página decida. Ignora (undefined) una invocación
 * mientras hay otra en vuelo; sin idCola rechaza con Error('Falta la cola').
 * @param {{ idCola: (number|string|null), ejercicio: (number|string) }} params - Cola y ejercicio
 * @returns {{
 *   registrando: boolean,
 *   registrarConPedido: (numero: string, incluirTodosDelCliente?: boolean) => Promise<Object|undefined>,
 *   registrarSinPedido: () => Promise<Object|undefined>
 * }} Estado y acciones de alta
 */
export const useRegistroTurno = ({ idCola, ejercicio }) => {
    const [registrando, setRegistrando] = useState(false);
    const enVuelo = useRef(false);
    const montado = useRef(true);

    useEffect(() => {
        montado.current = true;
        return () => {
            montado.current = false;
        };
    }, []);

    const registrar = useCallback(
        async (datos) => {
            if (!idCola) throw new Error("Falta la cola");
            if (enVuelo.current) return undefined;
            enVuelo.current = true;
            setRegistrando(true);
            try {
                return await ColasService.registrar(construirEntradaTurno({ ...datos, idCola, ejercicio }));
            } finally {
                enVuelo.current = false;
                if (montado.current) setRegistrando(false);
            }
        },
        [idCola, ejercicio]
    );

    const registrarConPedido = useCallback(
        (numero, incluirTodosDelCliente = false) =>
            registrar({ numero, pedidosCliente: incluirTodosDelCliente }),
        [registrar]
    );

    const registrarSinPedido = useCallback(() => registrar({ numero: null }), [registrar]);

    return { registrando, registrarConPedido, registrarSinPedido };
};
